import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpContext, HttpResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {JwtToken, TokenPair} from '../../_data/admin/jwt-token';
import {mapErrorToHttpResponse} from '../../_utils/operators';
import {NotificationService} from '../gui/notification.service';
import {ApiEndpoints} from '../../_constants/api-endpoints';
import {UserDetails} from '../../_data/mfa/mfa-user-details';
import {UserMigration} from '../../_data/admin/user-migration';
import {BYPASS_ERROR_INTERCEPTOR, JWT_TYPE_HEADER} from '../../_constants/constants';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private apiEndpoints = inject(ApiEndpoints);


  logIn(userName: string, password: string): Observable<[number, JwtToken<UserDetails>]> {
    console.debug(`Getting jwt token...`);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const headers = {Authorization: `Basic ${window.btoa(userName + ':' + password)}`};
    return this.http.get<JwtToken<UserDetails>>(
      this.apiEndpoints.securityAuthenticate,
      {observe: 'response', responseType: 'json', headers: headers, context: new HttpContext().set(BYPASS_ERROR_INTERCEPTOR, true)}
    ).pipe(
      mapErrorToHttpResponse(this.notificationService, [{statusCode: 401}, {statusCode: 418}, {statusCode: 423}]),
      tap(response => console.debug(`JWT Token response code: ${response?.status}`)),
      map((response: HttpResponse<JwtToken<UserDetails>>) => [response.status, response.body])
    );
  }

  logOut(): Observable<boolean> {
    console.debug('Logging out...');
    return this.http.get(this.apiEndpoints.securityLogout, {observe: 'response', responseType: 'text'}).pipe(map(response => response.status === 200));
  }

  refreshToken(jwtToken: JwtToken<any>): Observable<TokenPair<any, any>| number> {
    console.debug(`Refreshing jwt token "${jwtToken.token.substr(0, 10)}"...`);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return this.http.get<JwtToken<any>|number>(this.apiEndpoints.securityRefreshToken,{observe:'response',responseType:'json',headers: {Authorization: `Bearer ${jwtToken.token}`}})
      .pipe(
        mapErrorToHttpResponse(this.notificationService, [{statusCode: 401}, {statusCode: 418}, {statusCode: 423}]),
        map(refreshTokenResponse => {
          if(refreshTokenResponse.status === 200) {
            console.debug('JWT Token successfully refreshed.');
            return ({full: jwtToken, refresh: refreshTokenResponse.body} as TokenPair<any, any>);
          } else if(refreshTokenResponse.status === 418) {
            console.debug('JWT Token worked but login Access failed due to Time  Restriction...');
            return refreshTokenResponse.status;
          } else if(refreshTokenResponse.status === 423) {
            console.debug('JWT Token worked but login Access failed due to Account locked...');
            return refreshTokenResponse.status;
          } else {
            console.warn('JWT Token worked but refresh failed...');
            return null;
          }
        })
      );
  }

  userMigrated(userName: string): Observable<boolean> {
    console.debug(`Checking if user is migrated to the new TU Direct or needs to be redirected to the legacy`);
    return this.http.post<UserMigration>(
      this.apiEndpoints.securityUserMigration,
      {userName: userName},
      {headers: {ignoreLoadingBar: '', [JWT_TYPE_HEADER]: 'none'}}
    ).pipe(
      //If there is an error, map that to the user being migrated so that the UI doesn't do anything
      catchError(e => {
        console.error('Error checking user\'s migration status', e);
        return of({200: 'user migrated'} as UserMigration);
      }),
      //Invert the check so that anything other than "user not migrated" (including nulls) will make this stream work as if the user is migrated
      map(v => v?.['200'] !== 'user not migrated')
    );
  }

}
